import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, Container, Divider, List, ListItemButton, ListItemText, Paper, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import { tutorialsAPI } from '../services/api';
import { TutorialDetail, audienceText, clock, durationText } from '../components/help/helpTypes';

const errText = (e: any): string => e?.response?.data?.message || e?.message || 'Request failed';

/** One tutorial: the player (native video with captions), chapters, the written steps, the transcript and related tutorials. */
const TutorialPage: React.FC = () => {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const video = useRef<HTMLVideoElement>(null);
  const [t, setT] = useState<TutorialDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);

  useEffect(() => {
    setT(null);
    setError(null);
    void (async () => { try { setT(await tutorialsAPI.get(slug)); } catch (e) { setError(errText(e)); } })();
  }, [slug]);

  const jump = (seconds: number) => { if (video.current) { video.current.currentTime = seconds; void video.current.play(); } };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/help')} sx={{ mb: 2 }}>Help & Support</Button>
      {error && <Alert severity="error">{error}</Alert>}
      {t && (
        <>
          <Typography variant="h4" sx={{ mb: 0.5 }}>{t.title}</Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            {t.status === 'draft' && <Chip size="small" color="warning" label="Draft: only super users see this" />}
            {t.durationSeconds ? <Chip size="small" variant="outlined" label={durationText(t.durationSeconds)} /> : null}
            <Chip size="small" variant="outlined" label={audienceText(t.audience)} />
            <Chip size="small" variant="outlined" label={`Updated ${t.updatedAt}`} />
          </Stack>
          <Typography sx={{ mb: 3 }}>{t.description}</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: t.chapters.length ? '2fr 1fr' : '1fr' }, gap: 3, mb: 3 }}>
            <Box>
              {t.media ? (
                <Box component="video" ref={video} controls playsInline preload="metadata" crossOrigin="anonymous" poster={t.media.thumbnail || undefined}
                  sx={{ width: '100%', borderRadius: 1, bgcolor: 'common.black', aspectRatio: '16 / 9' }} aria-label={t.title}>
                  <source src={t.media.video} type="video/mp4" />
                  <track kind="captions" src={t.media.captions} srcLang={t.language} label="English" default />
                </Box>
              ) : (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', aspectRatio: '16 / 9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">The video for this tutorial is not ready yet. The written steps below are available now.</Typography>
                </Paper>
              )}
            </Box>
            {t.chapters.length > 0 && (
              <Paper variant="outlined">
                <Typography variant="subtitle2" sx={{ p: 1.5 }}>Chapters</Typography>
                <Divider />
                <List dense disablePadding>
                  {t.chapters.map((c) => (
                    <ListItemButton key={`${c.t}-${c.title}`} onClick={() => jump(c.t)} disabled={!t.media}>
                      <ListItemText primary={c.title} secondary={clock(c.t)} />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            )}
          </Box>

          <Typography variant="h6" sx={{ mb: 1 }}>Steps</Typography>
          <Box component="ol" sx={{ pl: 3, mt: 0, mb: 3 }}>
            {t.steps.map((s) => (
              <Box component="li" key={s.n} sx={{ mb: 1.5 }}>
                <Typography sx={{ fontWeight: 700 }}>{s.title}</Typography>
                <Typography variant="body2" color="text.secondary">{s.body}</Typography>
              </Box>
            ))}
          </Box>

          {t.transcript && (
            <Box sx={{ mb: 3 }}>
              <Button size="small" onClick={() => setShowTranscript((v) => !v)}>{showTranscript ? 'Hide transcript' : 'Show transcript'}</Button>
              {showTranscript && <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>{t.transcript}</Typography>}
            </Box>
          )}

          {t.related.length > 0 && (
            <>
              <Typography variant="h6" sx={{ mb: 1 }}>Related</Typography>
              <Stack spacing={1}>
                {t.related.map((r) => (
                  <Paper key={r.id} variant="outlined" sx={{ p: 1.5, cursor: 'pointer' }} onClick={() => navigate(`/help/${r.slug}`)}>
                    <Typography sx={{ fontWeight: 700 }}>{r.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{r.description}</Typography>
                  </Paper>
                ))}
              </Stack>
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default TutorialPage;
